import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Checkbox, Divider, Modal } from "antd";
import Search from "antd/lib/input/Search";
import { InfoMessages } from "constants/index";
import {
  DropDownDBFeatureInstance,
  DropdownEntityOption,
  TEditingEntity,
  TLinkSource
} from "components/feature-entity-linking-page/models";
import {
  getTLinkSourceSubmittalObject,
  translateFeatureToOptions
} from "components/feature-entity-linking-page/utils";
import { useEffect, useMemo, useState } from "react";
import BlueTickIcon from "components/svg-icons/blue-tick-icon";

export default function SubmittalListing({
  submittals,
  editingEntity,
  onUpdateEditingEntityList
}: {
  submittals: Array<DropDownDBFeatureInstance>;
  editingEntity: TEditingEntity;
  onUpdateEditingEntityList: (props: { updatedlist: TLinkSource[] }) => void;
}) {
  const [specNumber, setSpecNumber] = useState<string | null>();
  const [searchText, setSearchText] = useState<string>("");
  const [isSuggested, setSuggested] = useState(false);
  const [isAllSuggestedSelected, setAllSuggestedSelected] = useState(false);
  const [entities, setEntities] = useState<DropdownEntityOption[]>();
  const [selectedIds, setSelectedIds] = useState<
    Array<{ id: string; isSuggested: boolean | undefined }>
  >([]);

  const allEntities = useMemo(() => {
    if (editingEntity) {
      const allSubmittal = translateFeatureToOptions(
        submittals,
        editingEntity.entityDetail.specSection.section_number
      );

      setEntities(allSubmittal);

      return allSubmittal;
    }
    return [];
  }, [submittals, editingEntity]);

  useEffect(() => {
    setSpecNumber(editingEntity.entityDetail.specSection.section_number);
    const ids = editingEntity.list
      .map((x) =>
        x.feature_instance?.id
          ? {
              id: x.feature_instance.id,
              isSuggested: x.isSuggested
            }
          : undefined
      )
      .filter((x) => x !== undefined);

    const a = ids.filter((x) => x?.isSuggested).length;
    const b = allEntities.filter((x) => x.isSuggested).length;
    setAllSuggestedSelected(a === b);

    setSelectedIds(
      ids as {
        id: string;
        isSuggested: boolean | undefined;
      }[]
    );
  }, [editingEntity, allEntities]);

  useEffect(() => {
    let filterAfterSearch = allEntities.filter((x) =>
      x.label.includes(searchText)
    );
    if (isSuggested) {
      filterAfterSearch = filterAfterSearch.filter((x) => x.isSuggested);
    }
    setEntities(filterAfterSearch);
  }, [allEntities, isSuggested, searchText, specNumber]);

  const onChangeCheckbox = (checked: boolean, item: DropdownEntityOption) => {
    if (checked) {
      const insertSubmittal = () => {
        const sampleObject = getTLinkSourceSubmittalObject(item);
        onUpdateEditingEntityList({
          updatedlist: [...editingEntity.list, sampleObject]
        });
      };

      const isMaterialTracking = item.instance.feature_submittals.some(
        (x) => x.material_tracking
      );
      if (!isMaterialTracking) {
        Modal.confirm({
          title: "Do you want to link?",
          icon: <ExclamationCircleOutlined />,
          content: InfoMessages.confirmMsgForMaterialTrackingOff,
          className: "skip-listing-window",
          onOk() {
            insertSubmittal();
          },
          okText: "Yes",
          cancelText: "No"
        });
        return;
      }
      insertSubmittal();
    } else {
      onUpdateEditingEntityList({
        updatedlist: editingEntity.list.filter(
          (x) => x?.featureInstanceId !== item.id
        )
      });
    }
  };

  const btnCss = useMemo(() => {
    return isSuggested
      ? "text-ciq-blue-600 border-ciq-blue-600 focus:text-ciq-blue-600 focus:border-ciq-blue-600"
      : "text-black text-opacity-90 border-ciq-gray-100 focus:text-black text-opacity-90 focus:border-ciq-gray-100";
  }, [isSuggested]);

  return (
    <div className="flex flex-col h-full w-full features-card">
      <div className="flex items-center justify-between px-4">
        <div
          className="flex font-bold py-2"
          data-testid="submittallisting-title"
        >
          List of Submittals
        </div>
        <div className="flex space-x-1">
          {isSuggested && (
            <Checkbox
              checked={isAllSuggestedSelected}
              data-testid="submittallisting-checkbox-link-unlink-all"
              onChange={(e) => {
                // setAllSuggestedSelected(e.target.checked);
                if (e.target.checked) {
                  const suggestedObjects = allEntities
                    .filter((x) => x.isSuggested)
                    .map((item) => getTLinkSourceSubmittalObject(item));
                  const nonSuggestedLinkedObjects = editingEntity.list.filter(
                    (x) => !x.isSuggested
                  );
                  onUpdateEditingEntityList({
                    updatedlist: [
                      ...nonSuggestedLinkedObjects,
                      ...suggestedObjects
                    ]
                  });
                } else {
                  const nonSuggestedLinkedObjects = editingEntity.list.filter(
                    (x) => !x.isSuggested
                  );
                  onUpdateEditingEntityList({
                    updatedlist: nonSuggestedLinkedObjects
                  });
                }
              }}
            >
              {isAllSuggestedSelected ? "Unlink All" : "Link All"}
            </Checkbox>
          )}
        </div>
      </div>
      <div className="flex w-full pb-4 px-4">
        <Search
          placeholder="Search Submittal Name or ID"
          className="w-full pr-[10px]"
          onChange={(e) => {
            const text = (e.target.value || "").toLowerCase().trim();
            setSearchText(text);
          }}
          data-testid="submittallisting-search-submittal-name-id"
        />
        <Divider type="vertical" className="h-7 m-0 mr-4 pt-[35px]" />
        <Button
          icon={isSuggested ? <BlueTickIcon /> : null}
          className={`${btnCss} flex justify-center items-center  rounded p-1 gap-x-1 px-3`}
          onClick={() => setSuggested(!isSuggested)}
          data-testid="submittallisting-button-filter-suggestions"
        >
          <span>Filter Suggestions</span>
        </Button>
      </div>
      <div className="flex flex-col w-full overflow-y-auto h-full">
        {entities?.map((item) => (
          <div
            key={item.id}
            className="px-4 py-3 border-color border-0 border-b border-ciq-gray-400-alt-4 rounded-sm"
            data-testid="submittallisting-checkbox-item"
          >
            <Checkbox
              checked={selectedIds.some((x) => x.id === item.id)}
              onChange={(e) => {
                onChangeCheckbox(e.target.checked, item);
              }}
              data-testid="submittallisting-checkbox-item-checkbox"
            >
              <div className="flex items-center space-x-2">
                <i>{item.pre}</i>
                <div>{item.title}</div>
              </div>
            </Checkbox>
          </div>
        ))}
      </div>
    </div>
  );
}
